import React, { FC, useEffect } from 'react';
import { CrewProgressContainerStyled, ColumnContainerStyled, SeperatorStyled } from './styled';
import { Box, Text, Heading, Spinner, SpinnerSize } from '@bechtel/gds';
import { Trans, t } from '@lingui/macro';
import { CrewProgressTree } from './crew-progress-tree';
import { CrewMember, CrewProgressStatsFilterByTerm, FilterOperator, PersonaType } from '@bechtel/types';
import { find, get } from 'lodash';
import { ErrorRetry, useAppUser } from '@bechtel/client-common';
import { useCrewProgressStatsQuery } from '~/hooks/use-crew-progress-stats';
import { ReactComponent as NoDataIcon } from '@bechtel/client-assets/dist/images/empty-state_user-detail.svg';
import { useDashboardPermissions } from '@bechtel/client-dashboard-common';

interface CrewProgressProps {
  crewMember: CrewMember;
  isDashboardSelectedUser: boolean;
}

export const CrewProgress: FC<CrewProgressProps> = ({ crewMember, isDashboardSelectedUser }) => {
  const { user } = useAppUser();
  const { canViewAllCrewsStats } = useDashboardPermissions();
  const [loadCrewStats, { loading, data, error, refetch }] = useCrewProgressStatsQuery(true);
  const currentProjectWeek = find(user?.project?.projectConfiguration?.projectWeeks, (projectWeek) =>
    projectWeek?.isCurrent ? true : false
  );
  const loggedUserPersona = crewMember?.persona;
  const loggedUserId = crewMember?.id;

  useEffect(() => {
    if (!loggedUserId || loggedUserPersona === PersonaType.SUPER_ADMIN) return;
    const input = {
      complexFilter: {
        and: [
          {
            term: CrewProgressStatsFilterByTerm.INCLUDE_BLOCKED_COMMODITY,
            operator: FilterOperator.EQUALS,
            value: 'true',
          },
          {
            term: CrewProgressStatsFilterByTerm.START_DATE,
            operator: FilterOperator.GREATER_THAN,
            value: currentProjectWeek?.startDate,
          },
          {
            term: CrewProgressStatsFilterByTerm.END_DATE,
            operator: FilterOperator.LESS_THAN,
            value: currentProjectWeek?.endDate,
          },
        ],
      },
    };
    if (canViewAllCrewsStats && !isDashboardSelectedUser) {
      input.complexFilter.and.push({
        term: CrewProgressStatsFilterByTerm.PERSONA_TYPE,
        operator: FilterOperator.EQUALS,
        value: PersonaType.SUPERINTENDENT,
      });
    } else {
      input.complexFilter.and.push(
        {
          term: CrewProgressStatsFilterByTerm.PERSONA_TYPE,
          operator: FilterOperator.EQUALS,
          value: loggedUserPersona,
        },
        {
          term: CrewProgressStatsFilterByTerm.CREW_MEMBER_ID,
          operator: FilterOperator.EQUALS,
          value: loggedUserId,
        }
      );
    }
    loadCrewStats({
      variables: {
        input: input,
        paging: {
          limit: 50,
          cursor: '1',
        },
      },
    });
  }, [loggedUserId, loggedUserPersona, canViewAllCrewsStats]);

  const crewProgressStatsData = get(data, 'crewProgressStats.items', []);

  return (
    <CrewProgressContainerStyled data-testid="crew-progress-container" key={loggedUserId}>
      <Box py={{ sm: '500' }} px={{ sm: '450' }}>
        <Heading color="grey.1000" size="600" weight="600" data-testid="crew-progress">
          <Trans>Crew Progress</Trans>&nbsp;
        </Heading>
        <Text color="grey.1000" size="600" data-testid="this-week">
          <Trans>This Week</Trans>
        </Text>
      </Box>
      <ColumnContainerStyled>
        <Text size="200" color="grey.600" style={{ justifySelf: 'left' }} data-testid="team-member">
          <Trans>Team Member</Trans>
        </Text>
        <Text size="200" color="grey.600" data-testid="ready-to-weld">
          <Trans>Ready to Weld</Trans>
        </Text>
        <Text size="200" color="grey.600" data-testid="claimed">
          <Trans>Claimed</Trans>
        </Text>
        <Text size="200" color="grey.600" data-testid="vt-sent-back">
          <Trans>VT Sent Back</Trans>
        </Text>
      </ColumnContainerStyled>
      <SeperatorStyled />
      {loading && (
        <Box flexGrow={{ sm: 1 }} justifyContent={{ sm: 'center' }} p={{ sm: '500' }}>
          <Spinner size={SpinnerSize.LARGE} />
        </Box>
      )}
      {!loading && !error && (
        <Box style={{ overflowY: 'auto' }} flexDirection={{ sm: 'column' }} pb={{ sm: '500' }}>
          <CrewProgressTree depth={0} data={crewProgressStatsData} />
        </Box>
      )}
      {!loading && error && (
        <div data-testid="crew-data-error">
          <ErrorRetry refetch={refetch}>
            <Trans>There was a problem loading crew data.</Trans>
          </ErrorRetry>
        </div>
      )}
      {!loading && !error && crewProgressStatsData?.length === 0 && (
        <Box
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          flexDirection={{ sm: 'column' }}
          style={{ minHeight: '240px' }}
          flexGrow={{ sm: '1' }}
        >
          <NoDataIcon />
          <Box mt={{ sm: '600' }} px={{ sm: '700' }} flexShrink={{ sm: '0' }} justifyContent={{ sm: 'center' }}>
            <Text size="400" family="graphik" color="grey.1000" style={{ textAlign: 'center' }}>
              <Trans>Generate some Commodity and Rod Request data on your dashboard to see your team’s progress.</Trans>
            </Text>
          </Box>
        </Box>
      )}
    </CrewProgressContainerStyled>
  );
};
